import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

const TermsOfService = () => {
  const navigate = useNavigate();

  useEffect(() => {
    const title = "Terms of Service | YieldSocial";
    const description = "Terms of Service for using the YieldSocial app and website.";

    document.title = title;

    const metaDesc = document.querySelector('meta[name="description"]') || document.createElement('meta');
    metaDesc.setAttribute('name', 'description');
    metaDesc.setAttribute('content', description);
    if (!metaDesc.parentNode) document.head.appendChild(metaDesc);

    const canonical = document.querySelector('link[rel="canonical"]') || document.createElement('link');
    canonical.setAttribute('rel', 'canonical');
    canonical.setAttribute('href', window.location.href);
    if (!canonical.parentNode) document.head.appendChild(canonical);

    // JSON-LD structured data
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: title,
      description,
      url: window.location.href,
    });
    document.head.appendChild(ld);
    return () => {
      document.head.removeChild(ld);
    };
  }, []);

  return (
    <main className="min-h-screen bg-gradient-to-br from-green-50 to-amber-50">
      <section className="mx-auto max-w-3xl p-6 md:p-10">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-6 -ml-2"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Home
        </Button>
        
        <h1 className="text-3xl font-semibold tracking-tight text-gray-900 mb-4">Terms of Service</h1>
        <p className="text-sm text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>

        <div className="space-y-6 text-gray-800 leading-relaxed">
          <p>
            Welcome to YieldSocial. By accessing or using our website and applications (the "Service"),
            you agree to be bound by these Terms of Service (the "Terms"). If you do not agree to these
            Terms, do not use the Service.
          </p>

          <section>
            <h2 className="text-xl font-semibold mb-2">1. Accounts</h2>
            <p>
              You must provide accurate information and maintain the security of your account. You are
              responsible for all activity that occurs under your credentials. If you suspect unauthorized
              use, please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">2. Acceptable Use</h2>
            <p>
              You agree not to misuse the Service, including by attempting to access it using methods other
              than the provided interfaces; posting unlawful, infringing, or harmful content; or interfering
              with other users’ access.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">3. Content and Licenses</h2>
            <p>
              You retain ownership of content you create and share on the Service. By posting content, you
              grant YieldSocial a non-exclusive, worldwide license to host, display, and distribute that
              content solely to operate and improve the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">4. Privacy</h2>
            <p>
              Our collection and use of personal data is described in our <a className="underline" href="/privacy-policy">Privacy Policy</a>.
              By using the Service, you consent to such processing.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">5. Termination</h2>
            <p>
              We may suspend or terminate access to the Service if you materially or repeatedly breach these
              Terms. You may stop using the Service at any time. For instructions on deleting your account and
              data, see <a className="underline" href="/data-deletion">Data Deletion</a>.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">6. Disclaimers</h2>
            <p>
              The Service is provided "as is" without warranties of any kind. To the fullest extent permitted
              by law, we disclaim all implied warranties, including merchantability, fitness for a particular
              purpose, and non-infringement.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">7. Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, YieldSocial shall not be liable for any indirect,
              incidental, special, consequential, or punitive damages, or any loss of profits or revenues,
              whether incurred directly or indirectly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">8. Changes</h2>
            <p>
              We may update these Terms from time to time. We will post the updated Terms on this page with an
              updated date. Continued use of the Service constitutes acceptance of the revised Terms.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold mb-2">9. Contact</h2>
            <p>
              Questions about these Terms? Contact us at <a className="underline" href="mailto:nancy@truesty.com">nancy@truesty.com</a>.
            </p>
          </section>
        </div>
      </section>
    </main>
  );
};

export default TermsOfService;
